"use client";

import React, { useState } from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Loader as Loader2, Mail, Phone } from 'lucide-react';
import { motion } from "framer-motion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"];
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"];
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"];
export default function EstimateRequestForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType, setProjectType] = useState("Kitchen");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setZipError("");
    setConsentError("");

    // Validate 5-digit ZIP code
    const isFiveDigits = /^\d{5}$/.test(zip.trim());
    if (!isFiveDigits) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      return;
    }
    if (!agreed) {
      setConsentError("Please accept the contact agreement to proceed.");
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus("idle");
    const payload = new URLSearchParams();
    payload.append("name", name.trim());
    payload.append("phone", phone.trim());
    payload.append("email", email.trim());
    payload.append("project_type", projectType);
    payload.append("address", zip.trim());
    payload.append("zip", zip.trim());
    payload.append("details", message.trim());
    payload.append("timing", timing);
    payload.append("budget", budget);
    payload.append("contact_method", "Phone");
    payload.append("source", "Brookhaven Elevation website");
    payload.append("page", window.location.href);
    payload.append("form_location", "kitchen-remodeling-page");
    try {
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setSubmitStatus("success");
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="relative mx-auto max-w-4xl">
        <div className="text-center mb-10 md:mb-12">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground">
            Request Your Renovation Consultation
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Tell us about your home and envisioned space. We will review your goals and schedule your initial consultation.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-2xl border border-border bg-card/85 backdrop-blur-md p-6 sm:p-8 lg:p-10 shadow-xl">
          {submitStatus === "success" ? <div className="py-12 px-4 text-center">
              <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary/20 text-primary mb-4">
                <CheckCircle2 className="size-8 text-primary" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-medium text-foreground mb-3">
                Consultation Request Received
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-6 text-sm sm:text-base">
                Thank you for reaching out. A team member from Brookhaven Elevation Renovations will contact you shortly to review your project.
              </p>
              <Button variant="outline" onClick={() => {
            setSubmitStatus("idle");
            setName("");
            setPhone("");
            setEmail("");
            setZip("");
            setMessage("");
            setAgreed(false);
          }}>
                Send Another Request
              </Button>
            </div> : <form onSubmit={handleSubmit} className="space-y-6">
              {submitStatus === "error" && <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Unable to submit request online</AlertTitle>
                  <AlertDescription>
                    Please contact us directly by phone at{" "}
                    <a href="tel:4708479076" className="font-medium underline underline-offset-2 hover:opacity-80">
                      (470) 847-9076
                    </a>{" "}
                    or email{" "}
                    <a href="mailto:serviceleadalerts@gmail.com" className="font-medium underline underline-offset-2 hover:opacity-80">
                      serviceleadalerts@gmail.com
                    </a>
                    .
                  </AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                <div className="space-y-2">
                  <Label htmlFor="estimate-name">
                    Name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="estimate-name" type="text" required placeholder="Full name" value={name} onChange={e => setName(e.target.value)} className="bg-background/80" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimate-phone">
                    Phone <span className="text-destructive">*</span>
                  </Label>
                  <Input id="estimate-phone" type="tel" required placeholder="(470) 000-0000" value={phone} onChange={e => setPhone(e.target.value)} className="bg-background/80" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimate-email">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input id="estimate-email" type="email" required placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className="bg-background/80" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimate-zip">
                    ZIP Code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="estimate-zip" type="text" required maxLength={5} placeholder="30319" value={zip} onChange={e => {
                setZip(e.target.value);
                if (zipError) setZipError("");
              }} className="bg-background/80" />
                  {zipError && <p className="text-xs text-destructive mt-1">{zipError}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimate-project-type">
                    Project Type <span className="text-destructive">*</span>
                  </Label>
                  <select id="estimate-project-type" required value={projectType} onChange={e => setProjectType(e.target.value)} className="flex h-9 w-full min-w-0 rounded-md border border-input bg-background/80 px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm text-foreground">
                    {PROJECT_TYPE_OPTIONS.map((item, i) => <option key={item} value={item} data-index={i}>
                        {item}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimate-timing">Preferred Timing</Label>
                  <Select value={timing} onValueChange={val => setTiming(val)}>
                    <SelectTrigger id="estimate-timing" className="w-full bg-background/80">
                      <SelectValue placeholder="Select ideal timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMING_OPTIONS.map((item, i) => <SelectItem key={item} value={item} data-index={i}>
                          {item}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="estimate-budget">Budget Range</Label>
                  <Select value={budget} onValueChange={val => setBudget(val)}>
                    <SelectTrigger id="estimate-budget" className="w-full bg-background/80">
                      <SelectValue placeholder="Select anticipated investment" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGET_OPTIONS.map((item, i) => <SelectItem key={item} value={item} data-index={i}>
                          {item}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="estimate-message">Project Details</Label>
                  <Textarea id="estimate-message" rows={4} placeholder="Tell us about your home, layout changes, finishes, or questions..." value={message} onChange={e => setMessage(e.target.value)} className="bg-background/80 resize-y" />
                </div>
              </div>

              <div className="pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox id="estimate-consent" checked={agreed} onCheckedChange={checked => {
                setAgreed(checked === true);
                if (consentError) setConsentError("");
              }} className="mt-0.5" />
                  <Label htmlFor="estimate-consent" className="text-xs sm:text-sm font-normal text-muted-foreground leading-snug cursor-pointer select-none">
                    I agree to be contacted about my project by phone, text, or email.
                  </Label>
                </div>
                {consentError && <p className="text-xs text-destructive mt-1.5">{consentError}</p>}
              </div>

              <div className="pt-3">
                <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto min-w-48 bg-primary text-primary-foreground font-medium shadow-md hover:bg-primary/90">
                  {isSubmitting ? <>
                      <Loader2 className="size-4 animate-spin" />
                      Submitting...
                    </> : "Submit Request"}
                </Button>
              </div>
            </form>}

          <div className="mt-8 pt-6 border-t border-border/60 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <Phone className="size-3.5 text-primary" />
              <span>Direct Phone: </span>
              <a href="tel:4708479076" className="text-foreground hover:underline">
                (470) 847-9076
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="size-3.5 text-primary" />
              <span>Direct Email: </span>
              <a href="mailto:serviceleadalerts@gmail.com" className="text-foreground hover:underline">
                serviceleadalerts@gmail.com
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}